import Button from "@/lib/components/Button";
import Modal from "@/lib/components/Modal";
import ModalHeader from "@/lib/components/Modal/ModalHeader";
import Text from "@/lib/components/Text";
import { allHouseRules, allRoomTypes, filterAmenitiesAndFacilities } from "@/lib/constants/room";
import { SCREEN_SIZE } from "@/lib/constants/tailwind";
import { useLanguage } from "@/lib/hooks/useLanguage";
import { useTailwindBreakpoint } from "@/lib/hooks/useTailwindBreakpoint";
import { useTranslation } from "@/lib/i18n/client";
import { BookingType, Filter, FilterBookingType } from "@/lib/types/Search";
import { classNames } from "@/lib/utils/general";
import { breakpointIsLessThan } from "@/lib/utils/tailwind";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCheck, faMinus, faPlus, faSliders } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import PriceFilter from "./PriceFilter";
import { OnChangeMultiRangeSlider } from "./MultiRangeSlider/MultiRangeSlider";
import { FILTER_MAX_PRICE, FILTER_MIN_PRICE } from "@/lib/constants/search";
import { GoogleTagManagerEvents } from "@/lib/utils/googleTagEvents";
import { useAtomValue } from "jotai";
import { authAtom } from "@/lib/jotai/auth/authStore";
import { RoomProps } from "@/lib/components/Room";

type FilterCheckboxProps = {
    name: string;
    icon?: IconProp;
    checked: boolean;
    onClick: () => void;
};

const FilterCheckbox = ({ name, icon, checked, onClick }: FilterCheckboxProps) => {
    const [isHovered] = useState(false);

    return (
        <button className="flex flex-row gap-2 items-center mb-4" onClick={onClick}>
            <span
                className={`border border-grayAlpha-300 rounded-md h-7 w-7 overflow-hidden flex justify-center items-center`}
            >
                {(checked || isHovered) && (
                    <div className="h-full w-full rounded-md bg-cyan-500 flex justify-center items-center">
                        <FontAwesomeIcon icon={faCheck} size="sm" color="text-black" className="text-white" />
                    </div>
                )}
            </span>
            <Text as="span" className="text-black gap-2 mr-3 text-left">
                {icon && <FontAwesomeIcon color="text-black" className="mr-2" icon={icon} />}
                {name}
            </Text>
        </button>
    );
};

type NumberPicker = {
    defaultValue: number;
    onChange: (num: number) => void;
    maxValue?: number | null;
    label: string;
};
export type NumberPickerRef = {
    resetValue: () => void;
};

const NumberPicker = forwardRef<NumberPickerRef, NumberPicker>(
    ({ defaultValue, label, onChange, maxValue = null }, ref) => {
        const [value, setValue] = useState(defaultValue);
        const decrease = () => {
            setValue(Math.max(0, value - 1));
        };

        useImperativeHandle(ref, () => ({
            resetValue: () => {
                setValue(0);
            },
        }));

        const increase = () => {
            const incremented = Math.min(10, value + 1);
            if (maxValue && incremented > maxValue) {
                setValue(maxValue);
            } else {
                setValue(incremented);
            }
        };

        useEffect(() => {
            onChange(value);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [onChange, value]);

        return (
            <div className="w-full flex flex-row border border-gray-300 rounded-md text-base mb-4">
                <button onClick={decrease} className="basis-1/12 border-r border-r-gray-300 p-2.5 text-black">
                    <FontAwesomeIcon color="text-black" icon={faMinus} />
                </button>
                <Text as="span" className="grow p-2.5 text-center">
                    {value} {label}
                </Text>
                <button
                    disabled={maxValue === value}
                    onClick={increase}
                    className={`basis-1/12 p-2.5 border-l border-l-gray-300 text-black ${
                        maxValue === value ? " disabled:opacity-60 disabled:cursor-not-allowed bg-gray-200 " : " "
                    }`}
                >
                    <FontAwesomeIcon color="text-black" icon={faPlus} />
                </button>
            </div>
        );
    },
);

type FilterSectionProps = {
    title: string;
    children: React.ReactNode;
    borderStyle?: "dotted" | "solid" | "dashed";
};
const FilterSection = ({ title, borderStyle = "dashed", children }: FilterSectionProps) => {
    return (
        <div
            className={classNames(
                "w-full py-3 text-left flex flex-col gap-1  border-b-2 border-grayAlpha-300",
                `border-${borderStyle}`,
            )}
        >
            <Text as="span" className="text-black font-bold mb-6">
                {title}
            </Text>
            {children}
        </div>
    );
};

NumberPicker.displayName = "NumberPicker";

type SearcFilterProps = {
    onFilter: (filter: Filter) => void;
    ratesMin: number;
    ratesMax: number;
    defaultFilter: Filter;
    modalFullScreenBreakpoint: SCREEN_SIZE;
    rooms: RoomProps[];
};

type PriceFilter = {
    from: number;
    to: number;
};

const SearchFilter = ({ defaultFilter, onFilter, modalFullScreenBreakpoint, ratesMin, ratesMax }: SearcFilterProps) => {
    const lang = useLanguage();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const { t } = useTranslation(lang, "search");
    const screenSize = useTailwindBreakpoint()[1];

    const [selectedRoomTypes, setSelectedRoomTypes] = useState<string[]>([]);
    const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
    const [numberOfBeds, setNumberOfBeds] = useState<number>(0);
    const [numberOfBedrooms, setNumberOfBedrooms] = useState<number>(0);
    const [numberOfBathrooms, setNumberOfBathrooms] = useState<number>(0);
    const [bookingType, setBookingType] = useState<FilterBookingType>();
    const isMobile = breakpointIsLessThan(screenSize, modalFullScreenBreakpoint);
    const [hasFilterData, setHasFilterData] = useState(false);
    const [price, setPrice] = useState<PriceFilter>({
        from: Math.round(
            defaultFilter.price_from && defaultFilter.price_from > 0 ? defaultFilter.price_from : FILTER_MIN_PRICE,
        ),
        to: Math.round(
            defaultFilter.price_to && defaultFilter.price_to > 0 ? defaultFilter.price_to : FILTER_MAX_PRICE,
        ),
    });
    const numberOfBedsRef = useRef<NumberPickerRef>(null);
    const numberOfBedroomsRef = useRef<NumberPickerRef>(null);
    const numberOfBathroomsRef = useRef<NumberPickerRef>(null);

    const auth = useAtomValue(authAtom);
    const userIdForGTM = auth.isLoggedIn ? auth.userInfo._id : undefined;

    const onSelectRoomType = (roomType: string) => {
        if (selectedRoomTypes.includes(roomType)) {
            setSelectedRoomTypes(selectedRoomTypes.filter((x) => x !== roomType));
        } else {
            setSelectedRoomTypes([...selectedRoomTypes, roomType]);
        }
    };

    const onSelectAmenity = (amenity: string) => {
        if (selectedAmenities.includes(amenity)) {
            setSelectedAmenities(selectedAmenities.filter((x) => x !== amenity));
        } else {
            setSelectedAmenities([...selectedAmenities, amenity]);
        }
    };

    const onPriceFilterChange = ({ min, max }: OnChangeMultiRangeSlider) => {
        setPrice({
            from: min,
            to: max,
        });
    };

    const clearFilter = () => {
        setSelectedRoomTypes([]);
        setSelectedAmenities([]);
        setNumberOfBeds(0);
        setNumberOfBedrooms(0);
        setNumberOfBathrooms(0);
        setBookingType(undefined);
        setPrice({
            from: Math.round(
                defaultFilter.price_from && defaultFilter.price_from > 0 ? defaultFilter.price_from : FILTER_MIN_PRICE,
            ),
            to: Math.round(
                defaultFilter.price_to && defaultFilter.price_to > 0 ? defaultFilter.price_to : FILTER_MAX_PRICE,
            ),
        });
        if (numberOfBedsRef.current) {
            numberOfBedsRef.current.resetValue();
        }
        if (numberOfBedroomsRef.current) {
            numberOfBedroomsRef.current.resetValue();
        }
        if (numberOfBathroomsRef.current) {
            numberOfBathroomsRef.current.resetValue();
        }
    };

    const onApplyFilter = () => {
        const result: Filter = {
            roomType: selectedRoomTypes,
            amenities: selectedAmenities,
            bookingType: bookingType,
            showOnlyTopPartners: false,
            numberOfBeds,
            numberOfBedrooms,
            numberOfBathrooms,
            price_from: price.from,
            price_to: price.to,
        };

        try {
            GoogleTagManagerEvents.selectSerachFilter(lang, userIdForGTM);
            if (price.from || price.to) {
                GoogleTagManagerEvents.changePriceRangeFilter(
                    lang,
                    price.from ?? defaultFilter.price_from,
                    price.to ?? defaultFilter.price_to,
                    userIdForGTM,
                );
            }
            if (selectedAmenities.length) {
                GoogleTagManagerEvents.aminetiesAreSelected(lang, userIdForGTM);
            }
            if (selectedRoomTypes.length) {
                GoogleTagManagerEvents.selectRoomTypeFilter(lang, selectedRoomTypes.toString(), userIdForGTM);
            }

            const kidFirendly = allHouseRules[1].value,
                petFriendly = allHouseRules[0].value;
            if (selectedAmenities.includes(kidFirendly) || selectedAmenities.includes(petFriendly)) {
                GoogleTagManagerEvents.selectOneOrMoreHouseRules(lang, userIdForGTM);
            }
            if (bookingType) GoogleTagManagerEvents.clickOnInstantBookingFilter(lang, userIdForGTM);
        } catch (error) {
            console.log("triggering all GTM events relted to filter changes on search faield due to error:", error);
        }

        onFilter(result);
        setIsFilterOpen(false);
    };

    useEffect(() => {
        const filterDataExists =
            selectedRoomTypes.length > 0 ||
            selectedAmenities.length > 0 ||
            numberOfBeds > 0 ||
            numberOfBedrooms > 0 ||
            numberOfBathrooms > 0 ||
            bookingType !== undefined;
        // TODO add price filter to the condition
        //    || price.from !== ratesMin || price.to !== ratesMax
        setHasFilterData(filterDataExists);
    }, [
        selectedRoomTypes,
        selectedAmenities,
        numberOfBeds,
        numberOfBedrooms,
        numberOfBathrooms,
        bookingType,
        price,
        ratesMin,
        ratesMax,
    ]);

    useEffect(() => {
        if (defaultFilter) {
            setSelectedRoomTypes(defaultFilter.roomType ?? []);
            setSelectedAmenities(defaultFilter.amenities ?? []);
            setNumberOfBeds(defaultFilter.numberOfBeds ?? 0);
            setNumberOfBedrooms(defaultFilter.numberOfBedrooms ?? 0);
            setNumberOfBathrooms(defaultFilter.numberOfBathrooms ?? 0);
            setBookingType(defaultFilter.bookingType);
            setPrice({
                from: defaultFilter.price_from ?? FILTER_MIN_PRICE,
                to: defaultFilter.price_to ?? FILTER_MAX_PRICE,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div>
            <button
                className={`h-14 w-14 p-2 bg-white border rounded-md cursor-pointer ${
                    hasFilterData ? "border-2" : "border"
                } ${hasFilterData ? "border-cyan-500" : "border-gray-300"}`}
                onClick={() => setIsFilterOpen(true)}
            >
                <FontAwesomeIcon icon={faSliders} size="1x" />
            </button>
            {isFilterOpen && (
                <Modal
                    open={isFilterOpen}
                    mode={isMobile ? "full-screen" : "windowed"}
                    onClose={() => setIsFilterOpen(false)}
                    customScroll
                >
                    <div className="overflow-y-auto h-full lg:h-auto">
                        <ModalHeader
                            className="text-black p-6 pb-2"
                            onClose={() => setIsFilterOpen(false)}
                            title={t("filter.title")}
                        />
                        <div className="flex flex-col justify-between h-full px-6 py-2">
                            <div className="flex w-full flex-col justify-stretch items-start pb-20 lg:pb-4">
                                <FilterSection title={t("filter.room-type")}>
                                    {allRoomTypes.map((roomType) => (
                                        <FilterCheckbox
                                            onClick={() => onSelectRoomType(roomType.value)}
                                            checked={selectedRoomTypes.includes(roomType.value)}
                                            name={t(roomType.key)}
                                            key={roomType.value}
                                        />
                                    ))}
                                </FilterSection>
                                <FilterSection title={t("filter.price-filter.title")}>
                                    <PriceFilter
                                        onChange={onPriceFilterChange}
                                        defaultMin={defaultFilter.price_from}
                                        defaultMax={defaultFilter.price_to}
                                    />
                                </FilterSection>
                                <FilterSection title={t("filter.rooms-beds-title")}>
                                    <div className="flex flex-col md:flex-row justify-stretch gap-1 xl:gap-2">
                                        <NumberPicker
                                            defaultValue={numberOfBeds}
                                            ref={numberOfBedsRef}
                                            label={t("filter.room-bed.beds", { count: numberOfBeds })}
                                            onChange={(bedsNr) => {
                                                setNumberOfBeds(bedsNr);
                                            }}
                                            maxValue={8}
                                        />
                                        <NumberPicker
                                            defaultValue={numberOfBedrooms}
                                            ref={numberOfBedroomsRef}
                                            label={t("filter.room-bed.bedrooms", { count: numberOfBedrooms })}
                                            onChange={(bedroomsNr) => {
                                                setNumberOfBedrooms(bedroomsNr);
                                            }}
                                            maxValue={4}
                                        />
                                        <NumberPicker
                                            defaultValue={numberOfBathrooms}
                                            ref={numberOfBathroomsRef}
                                            label={t("filter.room-bed.bathrooms", { count: numberOfBathrooms })}
                                            onChange={(bathroomsNr) => {
                                                setNumberOfBathrooms(bathroomsNr);
                                            }}
                                            maxValue={2}
                                        />
                                    </div>
                                </FilterSection>
                                <FilterSection title={t("filter.houserules.title")}>
                                    {allHouseRules.map((houseRule) => (
                                        <FilterCheckbox
                                            onClick={() => {
                                                onSelectAmenity(houseRule.value);
                                            }}
                                            checked={selectedAmenities.includes(houseRule.value)}
                                            name={t(houseRule.key)}
                                            key={houseRule.value}
                                        />
                                    ))}
                                </FilterSection>
                                <FilterSection title={t("filter.bookingtype.title")}>
                                    <FilterCheckbox
                                        checked={bookingType === BookingType.Instant}
                                        name={t("filter.bookingtype.instant")}
                                        onClick={() => {
                                            bookingType
                                                ? setBookingType(undefined)
                                                : setBookingType(BookingType.Instant);
                                        }}
                                    />
                                </FilterSection>
                                <FilterSection title={t("filter.amenities.title")}>
                                    <div className="grid grid-flow-row md:grid-cols-3">
                                        {filterAmenitiesAndFacilities.map((amenity) => (
                                            <div key={amenity.value}>
                                                <FilterCheckbox
                                                    onClick={() => onSelectAmenity(amenity.value)}
                                                    checked={selectedAmenities.includes(amenity.value)}
                                                    name={t(amenity.key)}
                                                    key={amenity.value}
                                                    icon={amenity.icon}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </FilterSection>
                                <div
                                    className={`hidden xl:flex flex-row justify-start mt-2 gap-4 bg-white sticky bottom-0 w-full p-4 `}
                                >
                                    <Button
                                        variant="transparent"
                                        className="p-2 text-gray-300 border-grayAlpha-300 grow xl:grow-0"
                                        onClick={clearFilter}
                                    >
                                        {t("filter.button-clear-all")}
                                    </Button>
                                    <Button
                                        variant="orange"
                                        className="p-2 grow text-black  xl:grow-0"
                                        onClick={onApplyFilter}
                                    >
                                        {t("filter.button-apply-filters")}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex xl:hidden justify-between gap-2 px-6 py-4 bg-white border-gray-300 fixed bottom-0 left-0 right-0 shadow-lg-top z-10">
                        <Button
                            variant="transparent"
                            className="p-2 text-gray-300 border-grayAlpha-300  grow xl:grow-0"
                            onClick={clearFilter}
                        >
                            {t("filter.button-clear-all")}
                        </Button>
                        <Button variant="orange" className="p-2 grow text-black xl:grow-0" onClick={onApplyFilter}>
                            {t("filter.button-apply-filters")}
                        </Button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default SearchFilter;
